import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

export const chatRoles = ['system', 'user', 'assistant', 'tool'] as const;
export const chatRoleSchema = z.enum(chatRoles);
export type ChatRole = z.infer<typeof chatRoleSchema>;

export const conversationSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    agentId: idSchema,
    title: z.string().max(300).default('New conversation'),
  })
  .merge(timestampsSchema);
export type Conversation = z.infer<typeof conversationSchema>;

export const messageSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    conversationId: idSchema,
    role: chatRoleSchema,
    content: z.string(),
    toolCalls: z
      .array(
        z.object({
          id: z.string(),
          name: z.string(),
          arguments: z.record(z.unknown()),
        }),
      )
      .optional(),
  })
  .merge(timestampsSchema.partial());
export type Message = z.infer<typeof messageSchema>;

export const chatRequestSchema = z.object({
  agentId: idSchema,
  conversationId: idSchema.optional(),
  message: z.string().min(1),
  /** IDs of uploaded files to make available to the agent. */
  uploadIds: z.array(z.string()).optional(),
  /** When true, the server streams tokens via SSE. */
  stream: z.boolean().default(false),
});
export type ChatRequest = z.infer<typeof chatRequestSchema>;

/**
 * Unified Collaborate send: starts a durable run when none is active, or
 * queues the message into the active root run's graph context.
 */
export const chatMessageRequestSchema = z.object({
  agentId: idSchema,
  conversationId: idSchema.optional(),
  message: z.string().min(1),
  uploadIds: z.array(z.string()).optional(),
});
export type ChatMessageRequest = z.infer<typeof chatMessageRequestSchema>;

export const chatMessageResponseSchema = z.object({
  conversationId: idSchema,
  messageId: idSchema,
  /** Present when a new durable run was started. */
  runId: idSchema.optional(),
  status: z.enum(['started', 'queued', 'applied']),
});
export type ChatMessageResponse = z.infer<typeof chatMessageResponseSchema>;
